import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import StepIndicator from "./components/StepIndicator";
import ServiceStep from "./components/ServiceStep";
import DateTimeStep from "./components/DateTimeStep";
import ContactStep from "./components/ContactStep";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useServices } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";
import type { BookingDraft } from "./types";

const WEEKDAYS = [
  "Chủ nhật",
  "Thứ 2",
  "Thứ 3",
  "Thứ 4",
  "Thứ 5",
  "Thứ 6",
  "Thứ 7",
];

function formatDisplayDate(dateKey: string): string {
  if (!dateKey) return "";
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]}, ${d}/${m}/${y}`;
}

export default function Booking() {
  const { user, profile } = useAuth();
  const { data: services, loading, error: servicesError, reload } = useServices();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<BookingDraft>({
    serviceId: "",
    serviceName: "",
    bookingDate: "",
    bookingTime: "",
    customerName: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successRef, setSuccessRef] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setDraft((prev) => ({
        ...prev,
        customerName: prev.customerName || profile.name || "",
        phone: prev.phone || profile.phone || "",
        email: prev.email || profile.email || "",
      }));
    }
  }, [profile]);

  const selectService = (serviceId: string) => {
    const service = services.find((s) => s.id === serviceId);
    setDraft((prev) => ({
      ...prev,
      serviceId,
      serviceName: service?.shortName ?? "",
    }));
  };

  const next = () => {
    setError("");
    if (step === 1 && !draft.serviceId) {
      setError("Vui lòng chọn một dịch vụ để tiếp tục.");
      return;
    }
    if (step === 2 && (!draft.bookingDate || !draft.bookingTime)) {
      setError("Vui lòng chọn ngày và giờ hẹn.");
      return;
    }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setError("");
    if (!draft.customerName.trim() || !draft.phone.trim()) {
      setError("Vui lòng nhập đầy đủ họ tên và số điện thoại.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user_id: user?.id ?? null,
        customer_name: draft.customerName.trim(),
        phone: draft.phone.trim(),
        email: draft.email.trim() || null,
        service_id: draft.serviceId,
        service_name: draft.serviceName,
        booking_date: draft.bookingDate,
        booking_time: draft.bookingTime,
        notes: draft.notes.trim() || null,
        status: "pending",
      };

      let bookingId: string | null = null;

      if (user) {
        // Đã đăng nhập: có thể dùng .select() vì RLS cho phép SELECT theo user_id
        const { data, error: insertError } = await supabase
          .from("bookings")
          .insert(payload)
          .select("id");

        if (insertError) {
          console.error("Booking insert error:", insertError);
          setError(
            `Đặt lịch không thành công: ${insertError.message || "Lỗi không xác định"}. Vui lòng thử lại hoặc gọi hotline 0909 123 456.`
          );
          return;
        }
        bookingId = data?.[0]?.id ?? null;
      } else {
        // Chưa đăng nhập: chỉ insert, không select (vì RLS không có SELECT policy cho anon)
        const { error: insertError } = await supabase
          .from("bookings")
          .insert(payload);

        if (insertError) {
          console.error("Booking insert error:", insertError);
          setError(
            `Đặt lịch không thành công: ${insertError.message || "Lỗi không xác định"}. Vui lòng thử lại hoặc gọi hotline 0909 123 456.`
          );
          return;
        }
        // Tạo mã đặt lịch từ client cho khách chưa đăng nhập
        bookingId = crypto.randomUUID().slice(0, 8).toUpperCase();
      }

      setSuccessRef(bookingId);
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Có lỗi xảy ra. Vui lòng thử lại sau.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDraft({
      serviceId: "",
      serviceName: "",
      bookingDate: "",
      bookingTime: "",
      customerName: "",
      phone: "",
      email: "",
      notes: "",
    });
    setStep(1);
    setSuccessRef(null);
  };

  return (
    <MainLayout>
      <div className="w-full min-h-screen bg-background-50 pt-24 md:pt-28 pb-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground-950 mb-2">
              Đặt lịch hẹn
            </h1>
            <p className="text-sm md:text-base text-foreground-600 font-body">
              Trải nghiệm chăm sóc sức khỏe công nghệ cao chỉ với{" "}
              <strong className="text-primary-700">139.000đ</strong>
            </p>
          </div>

          {step < 4 && (
            <div className="mb-8">
              <StepIndicator currentStep={step} />
            </div>
          )}

          <div className="bg-white rounded-lg border border-background-200/70 p-5 md:p-8">
            {step === 1 && (
              <>
                <h2 className="font-display text-xl font-semibold text-foreground-950 mb-4">
                  Bạn muốn trải nghiệm dịch vụ nào?
                </h2>
                <ContentStatus loading={loading} error={servicesError} onRetry={reload}>
                <ServiceStep
                  services={services}
                  selectedServiceId={draft.serviceId}
                  onSelect={selectService}
                />
                </ContentStatus>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="font-display text-xl font-semibold text-foreground-950 mb-4">
                  Chọn ngày và giờ phù hợp
                </h2>
                <div className="mb-4 px-4 py-3 bg-background-100 rounded-md text-sm text-foreground-700 font-body">
                  <span className="font-medium text-foreground-900">
                    Dịch vụ:{" "}
                  </span>
                  {draft.serviceName}
                </div>
                <DateTimeStep
                  selectedDate={draft.bookingDate}
                  selectedTime={draft.bookingTime}
                  onDateChange={(date) =>
                    setDraft((prev) => ({ ...prev, bookingDate: date }))
                  }
                  onTimeChange={(time) =>
                    setDraft((prev) => ({ ...prev, bookingTime: time }))
                  }
                />
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="font-display text-xl font-semibold text-foreground-950 mb-4">
                  Thông tin liên hệ
                </h2>
                <div className="mb-4 px-4 py-3 bg-background-100 rounded-md text-sm text-foreground-700 font-body space-y-1">
                  <p>
                    <span className="font-medium text-foreground-900">
                      Dịch vụ:{" "}
                    </span>
                    {draft.serviceName}
                  </p>
                  <p>
                    <span className="font-medium text-foreground-900">
                      Thời gian:{" "}
                    </span>
                    {formatDisplayDate(draft.bookingDate)} - {draft.bookingTime}
                  </p>
                </div>
                <ContactStep
                  customerName={draft.customerName}
                  phone={draft.phone}
                  email={draft.email}
                  notes={draft.notes}
                  onNameChange={(v) =>
                    setDraft((prev) => ({ ...prev, customerName: v }))
                  }
                  onPhoneChange={(v) =>
                    setDraft((prev) => ({ ...prev, phone: v }))
                  }
                  onEmailChange={(v) =>
                    setDraft((prev) => ({ ...prev, email: v }))
                  }
                  onNotesChange={(v) =>
                    setDraft((prev) => ({ ...prev, notes: v }))
                  }
                />
              </>
            )}

            {step === 4 && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-5">
                  <i className="ri-check-double-line text-3xl" />
                </div>
                <h2 className="font-display text-2xl font-bold text-foreground-950 mb-3">
                  Đặt lịch thành công!
                </h2>
                <p className="text-sm md:text-base text-foreground-600 font-body max-w-md mx-auto mb-6">
                  Cảm ơn {draft.customerName}! Lịch hẹn của bạn đã được ghi nhận.
                  Chúng tôi sẽ liên hệ xác nhận trong thời gian sớm nhất.
                </p>
                <div className="max-w-sm mx-auto bg-background-100 rounded-lg p-5 text-left space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-500 font-label">Dịch vụ</span>
                    <span className="font-medium text-foreground-900">
                      {draft.serviceName}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-500 font-label">Thời gian</span>
                    <span className="font-medium text-foreground-900">
                      {formatDisplayDate(draft.bookingDate)} - {draft.bookingTime}
                    </span>
                  </div>
                  {successRef && (
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500 font-label">
                        Mã đặt lịch
                      </span>
                      <span className="font-medium text-foreground-900 font-mono">
                        {successRef.slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={reset}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line mr-2" />
                    Đặt thêm lịch khác
                  </button>
                  {user && (
                    <button
                      onClick={() => navigate("/profile")}
                      className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-800 border border-background-300/60 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                    >
                      Xem lịch sử đặt lịch
                    </button>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="mt-5 px-4 py-3 bg-accent-50 border border-accent-200 rounded-md text-sm text-accent-700 font-body flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {step < 4 && (
              <div className="flex items-center justify-between mt-8 pt-5 border-t border-background-200/70">
                {step > 1 ? (
                  <button
                    onClick={back}
                    className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-800 border border-background-300/60 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-2" />
                    Quay lại
                  </button>
                ) : (
                  <span />
                )}
                {step < 3 ? (
                  <button
                    onClick={next}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                  >
                    Tiếp tục
                    <i className="ri-arrow-right-line ml-2" />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={submitting}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2" />
                        Đang xử lý...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2" />
                        Xác nhận đặt lịch
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}